import { ContentCopy, Share } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import { getCollectionLink } from '../services/api.ts';

interface CollectionLinkProps {
  open: boolean;
  onClose: () => void;
}

const CollectionLink: React.FC<CollectionLinkProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Base URL for the collection form
  const baseUrl = `${window.location.origin}/collect`;
  
  // Full collection link
  const fullLink = linkCode ? `${baseUrl}/${linkCode}` : '';

  // Fetch the collection link when the dialog opens
  React.useEffect(() => {
    if (open) {
      fetchCollectionLink();
    }
  }, [open]);

  const fetchCollectionLink = async () => {
    setLoading(true);
    setError(null);
    try {
      const { link_code } = await getCollectionLink();
      setLinkCode(link_code);
    } catch (err) {
      setError('Failed to generate collection link. Please try again.');
      console.error('Error fetching collection link:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (fullLink) {
      navigator.clipboard.writeText(fullLink)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy:', err);
          setError('Failed to copy link. Please try manually selecting and copying.');
        });
    }
  };

  const handleShare = async () => {
    if (fullLink && navigator.share) {
      try {
        await navigator.share({
          title: 'Add your birthday info 🎂',
          text: 'Please add yourself to my Birthday Planner!',
          url: fullLink
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        keepMounted={false}
        PaperProps={{
          sx: { 
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Collect Birthdays
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Share this link with friends and family. When they fill out the form, their birthday will be automatically added to your collection!
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={36} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
              <Button 
                variant="outlined" 
                onClick={fetchCollectionLink}
                sx={{ borderRadius: 2 }}
              >
                Try Again
              </Button>
            </Box>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 2,
                  mb: 3,
                  background: 'rgba(0, 0, 0, 0.02)'
                }}
              >
                <TextField
                  fullWidth
                  variant="standard"
                  value={fullLink}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    sx: { px: 1 }
                  }}
                />
                <Tooltip title="Copy Link" placement="top">
                  <IconButton onClick={handleCopyLink} size="small">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Share />}
                  onClick={handleShare}
                  sx={{ 
                    borderRadius: 2,
                    px: 3
                  }}
                >
                  Share Link
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        
      </Dialog>
      
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default CollectionLink; 